import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Pagination,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Timeline as TimelineIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import EmailDialog from './EmailDialog';
import SubmitWU from './SubmitWU';

const StatusCard = ({ icon: Icon, count, label, color }) => (
  <Paper
    elevation={2}
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      height: '100%',
      borderLeft: `4px solid ${color}`,
    }}
  >
    <Icon sx={{ fontSize: 40, color: color }} />
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 500, color: color }}>
        {count}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Paper>
);

const SignatureDashboard = () => {
  const navigate = useNavigate();
  const [openNewDialog, setOpenNewDialog] = useState(false);
  const [newTransaction, setNewTransaction] = useState({
    name: '',
    description: '',
    message: ''
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [openSubmitWU, setOpenSubmitWU] = useState(false);

  const handleInputChange = (field) => (event) => {
    setNewTransaction({
      ...newTransaction,
      [field]: event.target.value
    });
  };

  const handleCreateTransaction = () => {
    if (newTransaction.name.trim()) {
      navigate('/signature-confirm', { 
        state: { transactionData: newTransaction } 
      });
      setOpenNewDialog(false);
      setNewTransaction({
        name: '',
        description: '',
        message: ''
      });
    }
  };

  const statusSummary = [
    {
      icon: EditIcon,
      count: 0,
      label: 'REQUIRES MY SIGNATURE',
      color: '#757575'
    },
    {
      icon: CheckCircleIcon,
      count: 16,
      label: 'COMPLETED',
      color: '#2e7d32'
    },
    {
      icon: WarningIcon,
      count: 1,
      label: 'EXPIRING SOON',
      color: '#d32f2f'
    },
    {
      icon: TimelineIcon,
      count: 3,
      label: 'IN PROGRESS',
      color: '#1976d2'
    }
  ];

  const allTransactions = [
    {
      id: 5,
      name: 'Auto & Tenant Insurance application',
      recipients: 'Michael Zhang, Emily Wang',
      createdDate: 'Jan 22nd, 2025',
      lastActivity: 'Jan 22nd, 2025',
      status: 'In Progress'
    },
    {
      id: 3,
      name: 'Commercial insurance application',
      recipients: 'Robert Chen',
      createdDate: 'Jan 23rd, 2025',
      lastActivity: 'Jan 23rd, 2025',
      status: 'Completed'
    },
    {
      id: 4,
      name: 'Rental Condo Insurance application',
      recipients: 'David Lee, Sarah Johnson',
      createdDate: 'Jan 21st, 2025',
      lastActivity: 'Jan 22nd, 2025',
      status: 'Completed'
    },
  ];

  const currentTransactions = allTransactions.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const getStatusChipProps = (status) => {
    switch (status) {
      case 'Completed':
        return {
          color: 'success',
          icon: <CheckCircleIcon />
        };
      case 'In Progress':
        return {
          color: 'primary',
          icon: <TimelineIcon />
        };
      default:
        return {
          color: 'default',
          icon: null
        };
    }
  };

  const handleRowClick = (transaction) => {
    // 移除行点击的导航功能
  };

  const handleEdit = (transaction) => {
    // 只有在 In Progress 状态时才能编辑
    if (transaction.status === 'In Progress') {
      navigate('/signature-confirm', { 
        state: { transactionData: transaction } 
      });
    }
  };

  const handleDownload = (transaction) => {
    console.log('Downloading:', transaction.name);
  };

  const handleEmail = (transaction) => {
    setSelectedTransaction(transaction);
    setOpenEmailDialog(true);
  };

  const handleSubmitWU = (transaction) => {
    setSelectedTransaction(transaction);
    setOpenSubmitWU(true);
  };

  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4,
        minHeight: 120  // 增加最小高度
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Home
        </Button>

        <Button
          variant="outlined"
          onClick={() => setOpenNewDialog(true)}
          startIcon={<AddIcon />}
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          NEW TRANSACTION
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1,
            maxWidth: '60%',
            mx: 'auto'
          }}
        >
          E-Signature Dashboard
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Manage and track all your electronic signature transactions
        </Typography>
      </Box>

      {/* Status Summary Section */}
      <Typography variant="h5" sx={{ mb: 2, color: '#1565c0' }}>
        My Transactions
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statusSummary.map((status, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatusCard {...status} />
          </Grid>
        ))}
      </Grid>

      {/* Recent Transactions Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2 
        }}>
          <Typography variant="h5" sx={{ color: '#1565c0' }}>
            Last 10 transactions
          </Typography>
        </Box>
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Transaction name</TableCell>
                <TableCell>Recipients</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentTransactions.map((transaction) => {
                const statusProps = getStatusChipProps(transaction.status);
                
                return (
                  <TableRow 
                    key={transaction.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: '#f5f5f5',
                      }
                    }}
                  >
                    <TableCell>{transaction.name}</TableCell>
                    <TableCell>{transaction.recipients}</TableCell>
                    <TableCell>{transaction.createdDate}</TableCell>
                    <TableCell>{transaction.lastActivity}</TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.status}
                        color={statusProps.color}
                        icon={statusProps.icon}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          gap: 1,
                          justifyContent: 'flex-end'
                        }}
                      >
                        {transaction.status === 'In Progress' ? (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(transaction)}
                              sx={{
                                color: '#1976d2',
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(transaction)}
                                sx={{
                                  color: '#1976d2',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                  },
                                }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Submit WU">
                              <IconButton
                                size="small"
                                onClick={() => handleSubmitWU(transaction)}
                                sx={{ color: '#1976d2' }}
                              >
                                <CloudUploadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination - 移除条件判断，始终显示 */}
        <Stack 
          spacing={2} 
          sx={{ 
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Pagination
            count={Math.max(1, Math.ceil(allTransactions.length / rowsPerPage))}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
            disabled={allTransactions.length <= rowsPerPage}
          />
        </Stack>
      </Box>

      {/* 新建交易对话框 */}
      <Dialog 
        open={openNewDialog} 
        onClose={() => setOpenNewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle 
          sx={{ 
            borderBottom: '1px solid #e0e0e0',
            mb: 2,
            pb: 2,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 2
          }}
        >
          <IconButton
            edge="start"
            onClick={() => setOpenNewDialog(false)}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6">Create New Transaction</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ display: 'block', mb: 2 }}
          >
            Fields marked with * are required.
          </Typography>
          
          <TextField
            autoFocus
            required
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={newTransaction.name}
            onChange={handleInputChange('name')}
            sx={{ mb: 3 }}
          />
          
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newTransaction.description}
            onChange={handleInputChange('description')}
            sx={{ mb: 3 }}
          />
          
          <TextField
            margin="dense"
            label="Message to all recipients (optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newTransaction.message}
            onChange={handleInputChange('message')}
          />
        </DialogContent>
        <DialogActions sx={{ 
          p: 3,
          borderTop: '1px solid #e0e0e0',
          gap: 1
        }}>
          <Button 
            onClick={() => setOpenNewDialog(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateTransaction}
            variant="contained"
            disabled={!newTransaction.name.trim()}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* 添加 Email Dialog */}
      <EmailDialog
        open={openEmailDialog}
        onClose={() => setOpenEmailDialog(false)}
        transaction={selectedTransaction}
      />

      <SubmitWU
        open={openSubmitWU}
        onClose={() => {
          setOpenSubmitWU(false);
          setSelectedTransaction(null);
        }}
        transactionName={selectedTransaction?.name}
      />
    </Container>
  );
};

export default SignatureDashboard; 